import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { ADDING, FAILED, PROCESSING, UPLOADING } from './useFileUploads';

const ICONS = {
    pdf: PictureAsPdfOutlinedIcon,
    word: DescriptionOutlinedIcon,
    slides: SlideshowOutlinedIcon,
    sheet: TableChartOutlinedIcon,
    file: InsertDriveFileOutlinedIcon
};

export const statusLabel = ({ status, progress, added = [], ready = 0 }) => {
    if (status === FAILED) return 'Upload failed';
    if (status === PROCESSING) return 'Processing…';
    if (status === ADDING) {
        return added.length > 1 ? `Adding ${ready} of ${added.length} slides…` : 'Adding to Image Q…';
    }
    return `Uploading… ${progress}%`;
};

/**
 * The inside of an Image Q tile for a file this user is uploading. It has no
 * slide number and no buttons, because it isn't a slide yet: just the picked
 * image (or an icon for a document the server turns into slides) under the
 * upload's progress. The drawer gives it the same frame as a slide's tile.
 */
export default function UploadTile({ upload, onRetry, onDismiss, horizontal = false }) {
    const { id, name, kind, previewUrl, status, progress, error, added = [], ready = 0 } = upload;
    const failed = status === FAILED;
    const determinate = status === UPLOADING || (status === ADDING && added.length > 1);
    const value = status === ADDING ? (ready * 100) / added.length : progress;
    const Icon = ICONS[kind] || ICONS.file;

    return (
        <Box data-testid='upload-tile' sx={{ position: 'absolute', inset: 0 }}>
            {previewUrl ? (
                <img
                    src={previewUrl}
                    alt={`Uploading ${name}`}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: 0.6 }}
                />
            ) : (
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'rgba(0, 0, 0, 0.6)',
                        px: 1,
                        pb: horizontal ? 3 : 5
                    }}>
                    <Icon aria-hidden sx={{ fontSize: horizontal ? 32 : 56 }} />
                </Box>
            )}
            <Box
                sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    px: 1,
                    py: 0.5,
                    background: 'rgba(255, 255, 255, 0.85)'
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        variant='caption'
                        title={name}
                        sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {name}
                    </Typography>
                    <Typography variant='caption' color={failed ? 'error' : 'textSecondary'} sx={{ flexShrink: 0 }}>
                        {statusLabel(upload)}
                    </Typography>
                </Box>
                {failed ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                            variant='caption'
                            color='error'
                            title={error}
                            sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {error}
                        </Typography>
                        <Button size='small' sx={{ minWidth: 0, py: 0 }} onClick={() => onRetry(id)} aria-label={`Retry uploading ${name}`}>
                            Retry
                        </Button>
                        <IconButton size='small' sx={{ p: 0.25 }} onClick={() => onDismiss(id)} aria-label={`Dismiss ${name}`}>
                            <CloseRoundedIcon fontSize='small' />
                        </IconButton>
                    </Box>
                ) : (
                    <LinearProgress
                        aria-label={`${statusLabel(upload)} ${name}`}
                        variant={determinate ? 'determinate' : 'indeterminate'}
                        value={determinate ? value : undefined}
                        sx={{ mt: 0.25 }}
                    />
                )}
            </Box>
        </Box>
    );
}
